import { create } from 'zustand';

/**
 * profileUiStore (2026-08-12) — 프로필 팝오버 · 설정 모달의 **표시 상태**.
 *
 * 설정 모달을 store 로 올린 이유: 진입점이 하나가 아니다(⑤ 레일 프로필 팝오버 · 대시보드
 * '내 프로필' 위젯 · 앞으로 늘어날 자리). 모달 본체는 AppLayout 이 하나만 들고, 진입점들은
 * 이 store 만 건드린다 — 화면마다 자기 모달을 복제하지 않기 위함(memoPanelStore 와 같은 구조).
 *
 * 영속하지 않는다 — 새로고침했는데 설정 창이 떠 있으면 놀란다(메모 패널과 같은 판단).
 */

/** 설정 모달 카테고리 — 좌측 내비 순서와 같다. */
export type SettingsCategory = 'account' | 'inbox' | 'preferences' | 'security' | 'mail' | 'notifications' | 'download';

export const SETTINGS_CATEGORIES: readonly SettingsCategory[] = [
  /*
   * ★ 'mail' 은 **뺐다** (2026-09-06 사용자 지시) — 그 갈래(기본·쓰기·읽기·외부 메일 계정)가
   *   전부 **메일 > 환경설정**에 있다. 같은 것을 두 자리에 두면 한쪽만 고쳐지는 날이 온다.
   *   ⚠ 타입·SETTINGS_SUBS 의 키는 남긴다 — 옛 주소로 들어오면 「어디로 갔는지」를 보여 준다.
   */
  /*
   * ★ 'inbox'(내 알림 — **받은 알림 목록**)는 2026-09-07 사용자 지시로 **내 정보 관리와
   *   환경설정 사이**에 선다. 아래 'notifications'(알림설정 — «어떤 알림을 받을지»)와는
   *   다른 것이다: 하나는 «온 것을 읽는 곳», 하나는 «올지 말지 정하는 곳».
   *   처음엔 알림설정의 하위로 넣었다가, 자주 여는 목록이 설정 안쪽에 묻혀 최상위로 올렸다.
   */
  'account', 'inbox', 'preferences', 'security', 'notifications', 'download',
];

/**
 * 카테고리 하위 메뉴 (2026-08-19 화면잠금 → 2026-08-20 전 카테고리 확장).
 *
 * 한 카테고리가 성격이 다른 블록을 여러 개 이고 있으면 스크롤로 밀려 아래가 안 보인다.
 * 특히 알림설정의 "화면별 알림" 은 등록된 메뉴 수만큼 행이 늘어나 그 아래가 영영 안 읽힌다.
 * 그래서 **화면을 나누기만** 한다 — 각 하위 화면의 내용은 지금까지 쓰던 섹션 그대로다.
 *
 * 빈 배열 = 하위 없음(한 화면). `download` 만 그렇다 — 행이 셋뿐이라 하위를 만들면 빈 껍데기가
 * 된다(2026-08-20 사용자 확정). 나머지 넷은 전부 갈랐다.
 */
export type SettingsSub =
  // 내 정보 관리
  | 'profile' | 'policy'
  // 환경설정
  | 'appearance' | 'theme' | 'calendar' | 'language'
  // 보안설정
  | 'password' | 'screen-lock' | 'lock-timing' | 'geo' | 'devices' | 'mobile'
  // 메일
  | 'mail-basic' | 'mail-write' | 'mail-read' | 'mailacc'
  // 알림설정
  | 'kinds' | 'external' | 'channels';

/** 카테고리별 하위 목록 — 배열 순서가 곧 내비 순서(SSOT). */
export const SETTINGS_SUBS: Record<SettingsCategory, readonly SettingsSub[]> = {
  account: ['profile', 'policy'],
  preferences: ['appearance', 'theme', 'calendar', 'language'],
  security: ['password', 'screen-lock', 'lock-timing', 'geo', 'devices', 'mobile'],
  // mailacc(외부 메일 계정)는 알림설정에서 여기로 **옮겨 왔다** — 알림과 상관없는 화면이었다.
  // ★ 'mailacc' 는 **메일 > 환경설정 > 외부메일** 로 옮겼다(2026-09-05 사용자 지시).
  //   메뉴에서만 뺀다 — 키는 남아 있어야 옛 링크(?sub=mailacc)가 「옮겼습니다」를 만난다.
  //   (「전체메일」을 메뉴에서만 뺐던 것과 같은 방식이다.)
  // ★ 메일은 통째로 **메일 > 환경설정**으로 옮겼다(2026-09-06) — 하위가 없다.
  mail: [],
  // 내 알림은 하위가 없다 — 한 화면이다(download 와 같다).
  inbox: [],
  notifications: ['kinds', 'external', 'channels'],
  download: [],
};

/** 카테고리를 처음 열었을 때 보이는 하위 — 하위가 생기기 전과 같은 화면이 오도록 골랐다. */
const DEFAULT_SUB: Record<SettingsCategory, SettingsSub | null> = {
  account: 'profile',
  inbox: null,
  preferences: 'appearance',
  security: 'password',
  mail: null,
  notifications: 'kinds',
  download: null,
};

interface ProfileUiState {
  /** ⑤ 레일 아바타 팝오버 열림 여부. */
  popoverOpen: boolean;
  /** 설정 모달 열림 여부. */
  settingsOpen: boolean;
  /**
   * 내 서명 모달 열림 여부 (2026-08-29 · 판 8 잔여 ②).
   *
   * ★ 판 8 은 서명을 **결재 시트에서만** 만들게 했다 — 결재하려다 설정으로 갔다 오면 무엇을
   *   하려 했는지 잊기 때문이다. 그 이유는 그대로 두고, **내 프로필에도 자리를 낸다**:
   *   결재할 일이 없을 때 미리 만들어 두려는 사람에게는 시트가 입구가 못 된다.
   */
  signatureOpen: boolean;
  /** 설정 모달의 활성 카테고리. 닫아도 유지 — 다시 열면 보던 곳으로 돌아온다. */
  settingsCategory: SettingsCategory;
  /** 카테고리별 활성 하위 메뉴. 카테고리와 같은 이유로 닫아도 유지한다. */
  settingsSub: Record<SettingsCategory, SettingsSub | null>;
  togglePopover: () => void;
  closePopover: () => void;
  /** 설정 모달 열기. 카테고리를 주면 그 자리에서 연다(팝오버는 함께 닫힌다). */
  openSettings: (category?: SettingsCategory, sub?: SettingsSub) => void;
  closeSettings: () => void;
  /** 내 서명 모달 열기 — 설정과 같은 이유로 팝오버는 함께 닫는다. */
  openSignature: () => void;
  closeSignature: () => void;
  setSettingsCategory: (category: SettingsCategory) => void;
  setSettingsSub: (category: SettingsCategory, sub: SettingsSub) => void;
}

export const useProfileUiStore = create<ProfileUiState>((set) => ({
  popoverOpen: false,
  settingsOpen: false,
  signatureOpen: false,
  settingsCategory: 'account',
  settingsSub: { ...DEFAULT_SUB },

  togglePopover: () => set((s) => ({ popoverOpen: !s.popoverOpen })),
  closePopover: () => set({ popoverOpen: false }),

  openSettings: (category, sub) => set((s) => ({
    settingsOpen: true,
    settingsCategory: category ?? s.settingsCategory,
    settingsSub: sub && category
      ? { ...s.settingsSub, [category]: sub }
      : s.settingsSub,
    // 모달이 팝오버를 덮으면 뒤에 남은 팝오버가 유령처럼 보인다 — 함께 닫는다.
    popoverOpen: false,
  })),
  closeSettings: () => set({ settingsOpen: false }),
  openSignature: () => set({ signatureOpen: true, popoverOpen: false }),
  closeSignature: () => set({ signatureOpen: false }),
  setSettingsCategory: (category) => set({ settingsCategory: category }),
  setSettingsSub: (category, sub) => set((s) => ({ settingsSub: { ...s.settingsSub, [category]: sub } })),
}));

/** 테스트 전용 — 카테고리·하위 선택을 처음 상태로. (선택은 세션 동안 유지되므로 테스트끼리 샌다) */
export function _resetProfileUiForTests(): void {
  useProfileUiStore.setState({
    popoverOpen: false,
    settingsOpen: false,
    signatureOpen: false,
    settingsCategory: 'account',
    settingsSub: { ...DEFAULT_SUB },
  });
}
